import { faSignIn, faSignOut, faUser } from '@fortawesome/pro-regular-svg-icons'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'components/Button'
import { Link } from 'components/Ui-Kits/Text/Link'
import { VerificationStatuses } from 'constants/user'
import { auth } from 'data/firebase'
import { signOut } from 'firebase/auth'
import { theme } from 'globalStyles'
import { useCurrentUserProfile } from 'hooks/data/useCurrentUserProfile'
import { useAppTranslation } from 'i18n/hooks'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import styled from 'styled-components'
import { getWebshopUrl } from 'utils/getWebshopUrl'
import { useClickOutside } from 'hooks/useClickOutside'
import { profileMenuItems } from 'constants/profileMenuItems'
import { Icon, ListItem, SubList as BaseSubList, SubListItem } from '../Navigation.styled'

export const Account = () => {
  const history = useHistory()
  const { user, loading, profile } = useCurrentUserProfile()
  const {
    t,
    i18n: { language },
  } = useAppTranslation()
  const [show, setShow] = useState(false)
  const menuRef = useRef(null)
  useClickOutside(menuRef, show, () => setShow(false))

  useEffect(() => {
    if (profile && profile.verificationStatus === VerificationStatuses.BLOCKED) {
      signOut(auth)
      history.push({
        pathname: '/auth/login',
        state: {
          blocked: true,
        },
      })
    }
  }, [profile, history])

  if (loading) return null

  if (user) {
    return (
      <StyledListItem ref={menuRef}>
        <UserIcon icon={faUser} onClick={() => setShow(s => !s)} />
        <UserMenu show={show} onClick={() => setShow(false)}>
          {profileMenuItems.map(({ translationKey, subpath }) => (
            <SubListItem key={subpath}>
              <MenuLink to={`/profile/${subpath}`}>{t(translationKey)}</MenuLink>
            </SubListItem>
          ))}
          <Divider />
          <SubListItem>
            <LogoutButton onClick={() => signOut(auth)}>
              {t('logOut')}
              <StyledIcon icon={faSignOut} />
            </LogoutButton>
          </SubListItem>
        </UserMenu>
      </StyledListItem>
    )
  }

  return (
    <StyledListItem ref={menuRef}>
      <StyledButton color="primary" onClick={() => setShow(show => !show)}>
        <span>{t('logIn')}</span>
        <Icon icon={faSignIn} />
      </StyledButton>
      <LoginSubList show={show}>
        <SubListItem>
          <Link as="a" href={getWebshopUrl(`${language}/my-account`)} target="_blank">
            {t('logInToTheOnlineStore')}
            <StyledIcon icon={faArrowRight} />
          </Link>
        </SubListItem>
        <SubListItem>
          <Link to="/auth/login">
            {t('logInToTheOnlineAuction')}
            <StyledIcon icon={faArrowRight} />
          </Link>
        </SubListItem>
      </LoginSubList>
    </StyledListItem>
  )
}

const UserIcon = styled(Icon)`
  cursor: pointer;
  font-size: 1.2rem;
`

const UserMenu = styled.ul`
  right: 0;
  left: auto;
  position: absolute;
  top: 100%;
  display: ${({ show }) => (show ? 'block' : 'none')};
  padding: 0.75rem 0;
  z-index: 101;
  min-width: 200px;
  list-style: none;
  white-space: nowrap;
  background-color: var(--color-white);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @media (min-width: 1024px) {
    border-top: 0.5rem solid var(--color-white);
  }
`

const MenuLink = styled(Link)`
  display: block;
  padding: 0.5rem 1.25rem;
  font-size: 0.9rem;
  color: var(--color-primary);
  text-decoration: none;

  &:hover {
    background-color: var(--color-grey);
  }
`

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.5rem 1.25rem;
  font-size: 0.9rem;
  color: var(--color-primary);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;

  &:hover {
    background-color: var(--color-grey);
  }
`

const Divider = styled.li`
  height: 1px;
  background-color: var(--color-grey);
  margin: 0.5rem 0;
`

const StyledButton = styled(Button)`
  font-size: 0.7rem;
  height: auto;
  padding: 0.5rem;
  line-height: 0.7rem;
  ${theme.breakpoints.phone} {
    font-size: 0.8rem;
    line-height: auto;
  }
  & > ${Icon} {
    margin-left: 0.5rem;
    font-size: 0.8rem;

    display: none;
    ${theme.breakpoints.phone} {
      display: inline-block;
    }
  }
`

const LoginSubList = styled(BaseSubList)`
  right: 0;
  left: auto;
  position: absolute;
  top: 100%;
  display: ${({ show }) => (show ? 'block' : 'none')};
  width: 100%;
  padding: 1rem;
  z-index: 101;

  @media (min-width: 1024px) {
    font-size: 0.8rem;
    line-height: auto;
    display: none;
    width: auto;
    border-top: 1rem solid var(--color-white);
  }
`

const StyledIcon = styled(FontAwesomeIcon)`
  margin-left: 0.5rem;
`

const StyledListItem = styled(ListItem)`
  position: static;

  @media (min-width: 1024px) {
    position: relative;
  }
`
